import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import {toast} from 'react-toastify'
import {useDispatch, useSelector} from 'react-redux'
import {kP11LinkService, getKickoffCallLabelFromCart} from './kp-11Link-service'
import {formatScheduledDateTime} from './scheduled-call-notice'
import {useAuthClient} from '../../utils/use-auth-client'
import {ADD_ON} from '../../utils/addons-data'
import {
  getScheduleDateWindow,
  getSeoScheduleOffsets,
  hasBookableDates,
  usesInfluencerScheduling,
} from '../../utils/partner-scheduling'
import {fetchProductGroups} from '../../redux/productRules/productRulesThunk'
import {selectAddonCodeMap} from '../../redux/productRules/productRulesSlice'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

export const CREATIVE_CALL_LABEL = 'Website Design Call'
export const DEFAULT_TIMEZONE_ID = 'America/Chicago'

const EMPTY_CART = {centers: [], apps: [], plans: [], oneTimeFees: []}
const EMPTY_SLOT_DETAILS = {
  availableTimes: [],
  serviceIdTitle: {},
  timeZone: {},
}

// Products whose presence (without anything else needing a call) lets the
// customer skip scheduling.
const PRODUCTS_REQUIRING_SCHEDULING = [
  'Thryv Leads',
  'Marketing Center',
  'SEO Money Back Guarantee',
  'SEO Non-Guaranteed',
  'SEO',
  'Social Ads',
  'Thryv HVAC',
  'Business Center',
  'Custom Video',
  'Professionally Designed Website',
]

// True when Workforce Center is in the cart and nothing else needs a call —
// the only case the customer may "Skip for Now".
export function isWorkforceOnlyCart(structuredCart) {
  const allCentersAndApps = [
    ...(structuredCart?.centers ?? []),
    ...(structuredCart?.apps ?? []),
  ]

  const hasWorkforceCenter = allCentersAndApps.some(item =>
    item?.name?.includes('Workforce Center'),
  )
  const hasWorkforceCenterInAddons = allCentersAndApps.some(item =>
    item?.addons?.some(addon => addon?.name?.includes('Workforce Center')),
  )

  const requiresScheduling = (name, included) =>
    name &&
    PRODUCTS_REQUIRING_SCHEDULING.some(
      product => name.includes(product) && !included,
    )

  const hasOtherSchedulingProducts = allCentersAndApps.some(item => {
    if (item?.name?.includes('Workforce Center')) return false
    if (requiresScheduling(item?.name, item?.included)) return true
    if (Array.isArray(item?.addons)) {
      return item.addons.some(
        addon =>
          addon?.name &&
          !addon.name.includes('Workforce Center') &&
          requiresScheduling(addon.name, addon.included),
      )
    }
    return false
  })

  return (
    (hasWorkforceCenter || hasWorkforceCenterInAddons) &&
    !hasOtherSchedulingProducts
  )
}

// Appends `&productGroupCodes=...` (the product groups in the cart that the
// available-times service-id rules key off) to the available-times url.
export function appendProductGroupCodes({
  callType,
  url,
  structuredCart,
  addonCodeMap = {},
  isInfluencerOps,
  isKeapPartner,
  isKickOffCallExist,
}) {
  const cart = structuredCart ?? EMPTY_CART
  const centers = cart.centers ?? []
  let allCentersAndApps = [
    ...centers,
    ...(cart.apps ?? []),
    ...(cart.plans ?? []),
    ...(cart.oneTimeFees ?? []).filter(
      ({name}) => name === ADD_ON.entitlements['Custom Video'].label,
    ),
  ]
  const ccEntitlements = centers
    .filter(({code}) => !code.startsWith('cc_'))
    .flatMap(item => item.entitlements)
  allCentersAndApps = [...allCentersAndApps, ...ccEntitlements]

  const onboardingAddons = ['HIPAA', ...(addonCodeMap.RestrictedAccess || [])]
  if (callType === 'kickoff') {
    onboardingAddons.push(
      ...(addonCodeMap.ThryvLeads || []),
      ...(addonCodeMap.MarketingCenter || []),
      ...(addonCodeMap.SEO || []),
      ...(addonCodeMap.SocialAds || []),
      ...(addonCodeMap.WorkforceCenter || []),
      ...(addonCodeMap.HomeServices || []),
      ...(addonCodeMap.ThryvSocialPosts || []),
      ...(addonCodeMap.Keap || []),
      ...(addonCodeMap.BusinessCenter || []),
    )
    if (isInfluencerOps) onboardingAddons.push(...(addonCodeMap.Website || []))
    if (isKeapPartner && isKickOffCallExist) {
      onboardingAddons.push(...(addonCodeMap.Website || []))
    }
  }

  if (callType === 'creative') {
    onboardingAddons.push(
      ...(addonCodeMap.Website || []),
      ...(addonCodeMap.Logo || []),
      ...(addonCodeMap.SEO || []),
    )
  }

  const codes = new Set()
  const addMatchingAddon = name => {
    if (!name) return
    const lower = name.toLowerCase()
    const matchedAddon = onboardingAddons.find(addon =>
      lower.includes(addon.toLowerCase()),
    )
    if (matchedAddon) codes.add(matchedAddon)
  }

  allCentersAndApps.forEach(item => {
    if (!item) return
    // add if item exists in cart (included or not)
    addMatchingAddon(item.code)
    // check if any of its addons are in cart
    if (Array.isArray(item.addons)) {
      item.addons.forEach(addon => addMatchingAddon(addon.code))
    }
  })

  const codeList = Array.from(codes)
  return codeList.length > 0
    ? `${url}&productGroupCodes=${codeList.join(',')}`
    : url
}

export function formatSlotTime(slot, timeZoneId) {
  const instant = slot?.startDateTime ?? slot?.startTime
  return timeZoneId
    ? dayjs(instant).tz(timeZoneId).format('h:mm A')
    : dayjs(instant).format('h:mm A')
}

// All state, rules and API calls for booking an onboarding / creative call for
// Partner 1:Many, Influencer and Keap Partner links. The view
// (scheduler-appointment-view.js) is purely presentational.
export function usePartnerScheduling({
  isCreativeCallExist,
  isKickOffCallExist,
  scheduleKickOffCallDetails,
  setScheduledKickOffCallDetails,
  scheduleCreativeCallDetails,
  setScheduledCreativeCallDetails,
  handleScheduleSuccessStatus,
  handleSkipScheduling,
  accountInformation,
  country = 'US',
  transitionData,
  structuredCart,
  // Open straight in "pick a new slot" mode — used when the customer chose
  // Reschedule on the success page.
  startInRescheduleMode = false,
}) {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const addonCodeMap = useSelector(selectAddonCodeMap)
  const cart = structuredCart ?? EMPTY_CART
  const requestIdRef = useRef(0)

  const [isSpanish, setIsSpanish] = useState(false)
  const [selectedCallLabel, setSelectedCallLabel] = useState('')
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [availableSlotDetails, setAvailableSlotDetails] =
    useState(EMPTY_SLOT_DETAILS)
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isRescheduling, setIsRescheduling] = useState(startInRescheduleMode)
  const isFirstResetRef = useRef(true)

  const kickoffCallLabel = getKickoffCallLabelFromCart(structuredCart)

  // Country from the partner link when present, otherwise the prop.
  const appointmentCountry =
    transitionData?.state?.keapPartnerLinkDetail?.country_code ||
    country ||
    'US'

  // Influencer and Partner 1:Many links share the influencer ops scheduling
  // rules (48h minimum, 15th cut-off, InfluencerOps service ids).
  const isInfluencerOps = usesInfluencerScheduling(
    transitionData?.state?.keapPartnerLinkDetail,
    transitionData?.state?.linkType,
  )
  // Keap Partner external (no influencer_name, but keapPartnerLinkDetail present)
  const isKeapPartner =
    !isInfluencerOps && !!transitionData?.state?.keapPartnerLinkDetail

  const seoApps = cart.apps?.filter(item => item?.name?.includes('SEO')) ?? []
  const isNewSEO = seoApps.length > 0
  const seoProductCodes = seoApps.map(item => item.code).join(',')

  // Same source as ooe-app's schedulers: the SEO window is days from today,
  // maintained in product_schedule_offset_days. A failed lookup is non-fatal —
  // the defaults in partner-scheduling.js (5 / 30 days) apply.
  const [scheduleOffsetDetails, setScheduleOffsetDetails] = useState([])
  useEffect(() => {
    let cancelled = false
    async function fetchScheduleOffsetDays() {
      try {
        const response = await client('cc/order/schedule-offset-days')
        if (!cancelled && Array.isArray(response)) {
          setScheduleOffsetDetails(response)
        }
      } catch {
        // non-fatal: the SEO defaults apply
      }
    }
    fetchScheduleOffsetDays()
    return () => {
      cancelled = true
    }
  }, [client])
  const {seoOffsetStartDays, seoOffsetEndDays} = useMemo(
    () =>
      getSeoScheduleOffsets(scheduleOffsetDetails, seoProductCodes.split(',')),
    [scheduleOffsetDetails, seoProductCodes],
  )

  useEffect(() => {
    dispatch(fetchProductGroups({client}))
  }, [dispatch, client])

  // --- which call(s) can be booked ---------------------------------------
  const scheduleOptions = useMemo(() => {
    const options = []
    if (isKickOffCallExist) {
      options.push({name: 'kickoff', label: kickoffCallLabel})
    }
    if (isCreativeCallExist) {
      options.push({name: 'creative', label: CREATIVE_CALL_LABEL})
    }
    return options
  }, [isKickOffCallExist, isCreativeCallExist, kickoffCallLabel])

  const activeCall =
    scheduleOptions.find(option => option.label === selectedCallLabel) ??
    scheduleOptions[0] ??
    null
  const isCreativeCall = activeCall?.name === 'creative'
  const callType = isCreativeCall ? 'creative' : 'kickoff'

  const activeScheduleDetails = isCreativeCall
    ? scheduleCreativeCallDetails
    : scheduleKickOffCallDetails
  const bookedCall = activeScheduleDetails?.isCallScheduled
    ? activeScheduleDetails.callDetails
    : null

  // What the "scheduled for ..." notice shows, in the zone the call was booked in.
  const bookedDateTime = useMemo(
    () => formatScheduledDateTime(bookedCall),
    [bookedCall],
  )

  // --- reset whenever the call / its booking changes ---------------------
  useEffect(() => {
    requestIdRef.current += 1
    setSelectedDate(null)
    setSelectedSlot(null)
    setIsLoadingSlots(false)
    // The first run is the mount: keep the reschedule mode we were opened in.
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false
    } else {
      setIsRescheduling(false)
    }
    setAvailableSlotDetails(EMPTY_SLOT_DETAILS)
  }, [
    activeCall?.name,
    scheduleKickOffCallDetails,
    scheduleCreativeCallDetails,
  ])

  // --- bookable dates ----------------------------------------------------
  const kickoffBookingDate =
    scheduleKickOffCallDetails?.callDetails?.thryvBookingDate
  const isKickoffScheduled = !!scheduleKickOffCallDetails?.isCallScheduled
  const dateWindow = useMemo(
    () =>
      getScheduleDateWindow({
        isInfluencerOps,
        country,
        isCreativeCallTabActive: isCreativeCall,
        isKickOffCallExist,
        isKickoffScheduled,
        kickoffBookingDate,
        isNewSEO,
        seoOffsetStartDays,
        seoOffsetEndDays,
      }),
    [
      isInfluencerOps,
      country,
      isCreativeCall,
      isKickOffCallExist,
      isKickoffScheduled,
      kickoffBookingDate,
      isNewSEO,
      seoOffsetStartDays,
      seoOffsetEndDays,
    ],
  )
  const hasDates = hasBookableDates(dateWindow)

  // --- language ----------------------------------------------------------
  const showLanguageToggle =
    appointmentCountry === 'US' &&
    activeCall?.name === 'kickoff' &&
    kickoffCallLabel.includes('Marketing Center')

  const changeLanguage = useCallback(spanish => {
    setIsSpanish(spanish)
    // Slots fetched under the previous language no longer apply.
    requestIdRef.current += 1
    setSelectedDate(null)
    setSelectedSlot(null)
    setIsLoadingSlots(false)
    setAvailableSlotDetails(EMPTY_SLOT_DETAILS)
  }, [])

  // --- slots -------------------------------------------------------------
  const selectDate = useCallback(
    async date => {
      const requestId = ++requestIdRef.current
      setSelectedDate(date)
      setSelectedSlot(null)
      setAvailableSlotDetails(EMPTY_SLOT_DETAILS)
      setIsLoadingSlots(true)

      const lang = isSpanish ? 'es' : 'en'
      const appointmentType = isCreativeCall ? 'Creative' : 'Onboarding'
      let url = `cc/order/available-times?country=${appointmentCountry}&address=${encodeURIComponent(
        accountInformation?.address,
      )}&languagePreference=${lang}&date=${dayjs(date).format(
        'YYYY-MM-DD',
      )}&keapClientPortalAccount=true&appointmentType=${appointmentType}&isInfluencerOps=${isInfluencerOps}`
      url = appendProductGroupCodes({
        callType,
        url,
        structuredCart,
        addonCodeMap,
        isInfluencerOps,
        isKeapPartner,
        isKickOffCallExist,
      })

      try {
        const data = await kP11LinkService.fetchAvailableTimeSlot(
          client,
          url,
          accountInformation,
        )
        if (requestId !== requestIdRef.current) return
        if (data?.warning) {
          toast.warning(data.warning)
        } else if (data) {
          setAvailableSlotDetails({
            availableTimes: data.availableTimes ?? [],
            serviceIdTitle: data.serviceIdTitle ?? {},
            timeZone: data.timeZone ?? {},
          })
        }
      } catch {
        if (requestId === requestIdRef.current) {
          toast.error('Unable to load available times. Please try again.')
        }
      } finally {
        if (requestId === requestIdRef.current) setIsLoadingSlots(false)
      }
    },
    [
      isSpanish,
      isCreativeCall,
      callType,
      appointmentCountry,
      accountInformation,
      isInfluencerOps,
      isKeapPartner,
      isKickOffCallExist,
      structuredCart,
      addonCodeMap,
      client,
    ],
  )

  // --- booking -----------------------------------------------------------
  // Releases the appointment that a reschedule replaces (legacy endpoint).
  const cancelAppointment = useCallback(
    async ({type, apptId, country: apptCountry}) => {
      try {
        await client('plans/creative', {
          data: {type, demoApptId: apptId, country: apptCountry},
          method: 'DELETE',
        })
      } catch {
        toast.error('error cancelling creative')
      }
    },
    [client],
  )

  const confirmScheduling = useCallback(async () => {
    try {
      setIsConfirming(true)
      const apptData = {
        firstName: accountInformation.firstName,
        lastName: accountInformation.lastName,
        email: accountInformation.email,
        phone: accountInformation?.mobile,
        company: accountInformation.company,
        isKeapClientPortalReq: true,
      }
      const scheduleInfo = {
        timeZone: availableSlotDetails.timeZone.timeZoneId,
        serviceId: availableSlotDetails?.serviceIdTitle.serviceId,
        staffId: selectedSlot.staffId,
        startTime: selectedSlot.startTime,
        thryvBookingDate: selectedSlot.startDateTime,
        isAus: appointmentCountry === 'AU',
        isNZ: appointmentCountry === 'NZ',
        country: appointmentCountry,
        isApptHold: true,
      }

      const appointmentType = isCreativeCall ? 'creative' : 'onboarding'
      apptData[appointmentType] = scheduleInfo
      const existingApptId = bookedCall?.apptId ?? ''

      // Book the new slot first; only release the old one once it succeeded.
      const {apptId} = await kP11LinkService.confirmScheduleSlot(
        client,
        `plans/${appointmentType}`,
        apptData,
      )
      if (apptId) apptData.appointment_type = appointmentType

      const details = {
        isCallScheduled: true,
        callDetails: {
          ...apptData[appointmentType],
          appointment_type: appointmentType,
          apptId,
        },
      }

      if (appointmentType === 'onboarding') {
        setScheduledKickOffCallDetails(details)
        sessionStorage.setItem(
          'scheduleKickOffCallDetails',
          JSON.stringify(details),
        )
        if (scheduleCreativeCallDetails?.isCallScheduled) {
          setScheduledCreativeCallDetails(null)
          sessionStorage.removeItem('scheduleCreativeCallDetails')
        }
      } else {
        setScheduledCreativeCallDetails(details)
        sessionStorage.setItem(
          'scheduleCreativeCallDetails',
          JSON.stringify(details),
        )
      }

      if (existingApptId) {
        await cancelAppointment({
          type: appointmentType,
          country: appointmentCountry,
          apptId: existingApptId,
        })
      }

      setIsConfirming(false)
      if (appointmentType === 'onboarding' && scheduleOptions.length > 1) {
        setSelectedCallLabel(scheduleOptions[1].label)
      } else {
        handleScheduleSuccessStatus()
      }
      toast.success(`${activeCall?.label ?? 'Call'}  confirmed`)
    } catch (err) {
      setIsConfirming(false)
      if (err.status !== 400) {
        throw err
      }
      err.errors.forEach(({message}) => toast.error(message))
    }
  }, [
    accountInformation,
    availableSlotDetails,
    selectedSlot,
    appointmentCountry,
    isCreativeCall,
    bookedCall,
    client,
    cancelAppointment,
    setScheduledKickOffCallDetails,
    setScheduledCreativeCallDetails,
    scheduleCreativeCallDetails,
    scheduleOptions,
    handleScheduleSuccessStatus,
    activeCall,
  ])

  // "Skip for Now". If a call is already booked (the customer got here through
  // Reschedule), skipping must really mean no call: release the booked
  // appointment first, clear it from the page, and only then report the skip.
  // If a release fails we stay put — showing "skipped" while the booking is
  // still held (and would be saved with the order) would be wrong.
  const skipScheduling = useCallback(async () => {
    if (!handleSkipScheduling) return

    const booked = [
      {
        type: 'onboarding',
        details: scheduleKickOffCallDetails,
        clear: () => {
          setScheduledKickOffCallDetails?.(null)
          sessionStorage.removeItem('scheduleKickOffCallDetails')
        },
      },
      {
        type: 'creative',
        details: scheduleCreativeCallDetails,
        clear: () => {
          setScheduledCreativeCallDetails?.(null)
          sessionStorage.removeItem('scheduleCreativeCallDetails')
        },
      },
    ].filter(
      ({details}) => details?.isCallScheduled && details.callDetails?.apptId,
    )

    if (booked.length) {
      setIsConfirming(true)
      try {
        for (const {type, details, clear} of booked) {
          await client('plans/creative', {
            data: {
              type,
              demoApptId: details.callDetails.apptId,
              country: details.callDetails.country || appointmentCountry,
            },
            method: 'DELETE',
          })
          clear()
        }
      } catch {
        toast.error(
          'We could not cancel your booked call, so scheduling was not skipped. Please try again.',
        )
        return
      } finally {
        setIsConfirming(false)
      }
    }

    handleSkipScheduling()
  }, [
    handleSkipScheduling,
    scheduleKickOffCallDetails,
    scheduleCreativeCallDetails,
    setScheduledKickOffCallDetails,
    setScheduledCreativeCallDetails,
    appointmentCountry,
    client,
  ])

  const timeZoneId = availableSlotDetails.timeZone?.timeZoneId
  const displayTimeZoneId = timeZoneId || DEFAULT_TIMEZONE_ID
  const timeZoneLabel = dayjs(selectedDate ?? undefined)
    .tz(displayTimeZoneId)
    .format('z')

  const slots = availableSlotDetails.availableTimes.map(slot => ({
    ...slot,
    displayTime: formatSlotTime(slot, timeZoneId),
  }))

  const isSameAsBooked =
    !!bookedCall &&
    !!selectedSlot &&
    bookedCall.thryvBookingDate === selectedSlot.startDateTime

  return {
    // calls / tabs
    scheduleOptions,
    activeCall,
    selectCall: setSelectedCallLabel,
    // language
    showLanguageToggle,
    isSpanish,
    changeLanguage,
    // calendar
    dateWindow,
    hasDates,
    selectedDate,
    selectDate,
    // slots
    slots,
    selectedSlot,
    selectSlot: setSelectedSlot,
    isLoadingSlots,
    timeZoneLabel,
    // booking
    bookedDateTime,
    isRescheduling,
    startRescheduling: () => setIsRescheduling(true),
    // Keep the current appointment: drop the half-made selection and leave
    // reschedule mode without booking or releasing anything. With a single
    // call there is nothing else to do on this page, so go back to the
    // confirmation.
    stopRescheduling: () => {
      requestIdRef.current += 1
      setIsRescheduling(false)
      setIsLoadingSlots(false)
      setSelectedDate(null)
      setSelectedSlot(null)
      setAvailableSlotDetails(EMPTY_SLOT_DETAILS)
      if (scheduleOptions.length === 1) handleScheduleSuccessStatus?.()
    },
    isConfirming,
    canConfirm:
      !!selectedDate && !!selectedSlot && !isConfirming && !isSameAsBooked,
    confirmScheduling,
    // skip
    canSkip: !!handleSkipScheduling && isWorkforceOnlyCart(cart),
    skipScheduling,
  }
}
